import _ from 'lodash';

import Locator from '../../../../shared/Locator';
import { useAddJobToNotificationQueue } from '../../../components/JobNotificationHub';
import { useAddToast } from '../../../contexts/ToastContext';
import { queryClient, useMutation } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { Build } from '../../../types';

type RebuildRevisionQuery = {
  action?: 'dependencies';
};

type RebuildRevisionVariables = {
  revisionLocator: string;
  query?: RebuildRevisionQuery;
};

//  Note that the response won't have actual Error instances,
//  because Express will serialize the error.
type RebuildRevisionResponse = Array<Build | Error>;

//  Function for rebuilding a revision.
async function rebuildRevision({ revisionLocator, query }: RebuildRevisionVariables) {
  const response = await get<RebuildRevisionResponse>(`/api/revisions/${nuc(revisionLocator)}/rebuild`, false, query);
  return response.data as RebuildRevisionResponse;
}

/**
 * Mutation which will rebuild a revision.
 *
 * Features:
 * - Shows an info toast when build starts
 * - Automatically adds the build job to the notification queue
 * - Returns `isPending` state (to replace Reflux state Revision.status.submitting_build)
 * - Invalidates revision queries on success
 *
 * @example
 * const { mutateAsync: rebuild, isPending: isRebuilding } = useRebuildRevision();
 *
 * await rebuild({
 *   revisionLocator: 'custom+1/my-project$1.0.0'
 * });
 */
export default function useRebuildRevision() {
  const onAddToast = useAddToast();
  const addJob = useAddJobToNotificationQueue();

  return useMutation<RebuildRevisionVariables, RebuildRevisionResponse>({
    mutationFn: rebuildRevision,
    onMutate: (variables: RebuildRevisionVariables) => {
      const revision = Locator.parse(variables.revisionLocator).revision ?? 'revision';
      const displayRevision = revision.length > 10 ? `${revision.slice(0, 10)}...` : revision;
      //  Let the user know what is happening.
      onAddToast({
        type: 'info',
        content: `Analyzing "${displayRevision}".`,
      });
    },
    onSuccess: (buildsOrErrors: RebuildRevisionResponse, variables: RebuildRevisionVariables) => {
      if (buildsOrErrors) {
        buildsOrErrors.forEach((buildOrError) => {
          if (_.isError(buildOrError)) {
            return;
          }
          // Queue a job notification for the build.
          const { task, jobToken } = buildOrError.task ?? {};
          if (jobToken && task) {
            addJob(jobToken, task);
          }
        });
      }

      //  Invalidate the revision data for this locator.
      queryClient.invalidateQueries({ queryKey: ['revision', variables.revisionLocator] });
    },
  });
}
